import { DifficultyId, EnemyType, ObstacleType } from './types.js';

// Simulation
export const SERVER_TICK_RATE = 60;
export const SERVER_PATCH_RATE = 20;
export const FIXED_TIMESTEP = 1 / SERVER_TICK_RATE;

// Road / vehicle
export const ROAD_HALF_WIDTH = 6.0;
export const MAX_LATERAL_OFFSET = 4.5;
export const BASE_SPEED = 22.0;
export const BOOST_SPEED = 34.0;
export const BRAKE_SPEED = 12.0;
export const MAX_BOOST_CHARGE = 100;
export const BOOST_DRAIN_PER_SECOND = 28;
export const BOOST_RECHARGE_PER_SECOND = 9;
export const BRAKE_RECHARGE_PER_SECOND = 22;
export const LATERAL_SPEED = 7.5; // m/s across the road at full steering lock
export const VEHICLE_COLLISION_HALF_WIDTH = 1.0;
export const VEHICLE_COLLISION_HALF_LENGTH = 2.2;

// Combat
export const VEHICLE_MAX_HEALTH = 100;
export const REPAIR_KIT_HEALTH = 30;
export const DRIVER_WEAPON_DAMAGE = 8;
export const PASSENGER_WEAPON_DAMAGE = 12;
export const DRIVER_FIRE_COOLDOWN_MS = 130;
export const PASSENGER_FIRE_COOLDOWN_MS = 180;
export const WEAPON_MAX_RANGE = 320;

// Enemy behavior tuning, per type
export interface EnemyConfig {
  health: number;
  radius: number;          // hit sphere radius (m)
  hoverHeight: number;     // mesh/aim center height above road (m)
  preferredDistance: number; // how far ahead of the player they try to sit (m)
  catchupSpeed: number;    // extra m/s available to close the gap
  weaveAmplitude: number;  // lateral sine weave (m)
  weaveSpeed: number;      // weave angular speed (rad/s)
  fireIntervalMs: number;  // average time between attacks
  telegraphMs: number;     // warning time before the shot lands
  damage: number;          // damage to vehicle per hit
  score: number;           // base score on kill
}

export const ENEMY_CONFIGS: Record<EnemyType, EnemyConfig> = {
  [EnemyType.Sedan]: {
    health: 30, radius: 2.0, hoverHeight: 0.7,
    preferredDistance: 30, catchupSpeed: 14,
    weaveAmplitude: 1.6, weaveSpeed: 0.9,
    fireIntervalMs: 4600, telegraphMs: 900, damage: 6, score: 100,
  },
  [EnemyType.Motorcycle]: {
    health: 14, radius: 1.4, hoverHeight: 0.8,
    preferredDistance: 14, catchupSpeed: 20,
    weaveAmplitude: 2.4, weaveSpeed: 1.8,
    fireIntervalMs: 3800, telegraphMs: 750, damage: 4, score: 150,
  },
  [EnemyType.Truck]: {
    health: 90, radius: 2.8, hoverHeight: 1.4,
    preferredDistance: 42, catchupSpeed: 9,
    weaveAmplitude: 0.7, weaveSpeed: 0.5,
    fireIntervalMs: 5600, telegraphMs: 1100, damage: 12, score: 300,
  },
  [EnemyType.Drone]: {
    // cheap aerial harasser: fast, jittery, dies to a sneeze
    health: 16, radius: 1.2, hoverHeight: 3.6,
    preferredDistance: 20, catchupSpeed: 26,
    weaveAmplitude: 3.8, weaveSpeed: 2.6,
    fireIntervalMs: 3400, telegraphMs: 700, damage: 4, score: 200,
  },
  [EnemyType.Helicopter]: {
    health: 120, radius: 2.6, hoverHeight: 7.0,
    preferredDistance: 55, catchupSpeed: 24,
    weaveAmplitude: 3.4, weaveSpeed: 0.7,
    fireIntervalMs: 4000, telegraphMs: 850, damage: 8, score: 500,
  },
  [EnemyType.Boss]: {
    health: 420, radius: 3.6, hoverHeight: 8.0,
    preferredDistance: 60, catchupSpeed: 30,
    weaveAmplitude: 5.0, weaveSpeed: 0.5,
    fireIntervalMs: 2600, telegraphMs: 800, damage: 9, score: 2000,
  },
  [EnemyType.ArmoredTarget]: {
    health: 520, radius: 2.5, hoverHeight: 1.2,
    preferredDistance: 38, catchupSpeed: 22,
    weaveAmplitude: 2.2, weaveSpeed: 0.55,
    fireIntervalMs: 3200, telegraphMs: 950, damage: 10, score: 2500,
  },
};

// Road obstacles — static hazards the driver weaves through (or shoots)
export interface ObstacleConfig {
  radius: number;       // collision + hit-sphere radius (m)
  height: number;       // aim center height (m)
  health: number;       // shootable; barrels pop instantly
  vehicleDamage: number;
  score: number;        // reward for shooting it before it's a problem
  repairAmount?: number; // optional roadside pickup heal on destruction
}

export const OBSTACLE_CONFIGS: Record<ObstacleType, ObstacleConfig> = {
  [ObstacleType.Barrel]: { radius: 0.8, height: 0.6, health: 1, vehicleDamage: 5, score: 25 },
  [ObstacleType.Barrier]: { radius: 1.6, height: 0.6, health: 50, vehicleDamage: 8, score: 50 },
  [ObstacleType.Rock]: { radius: 1.3, height: 0.8, health: 70, vehicleDamage: 10, score: 50 },
  [ObstacleType.Wreck]: { radius: 1.8, height: 0.8, health: 60, vehicleDamage: 10, score: 50 },
  [ObstacleType.RepairKit]: {
    radius: 1.05,
    height: 1.05,
    health: 1,
    vehicleDamage: 0,
    score: 0,
    repairAmount: REPAIR_KIT_HEALTH,
  },
};

export const ENEMY_SPAWN_AHEAD = 130;   // meters ahead of vehicle when spawning
export const ENEMY_ENGAGE_MARGIN = 12;  // enemies attack only within preferredDistance + this (m)
export const ATTACK_SPACING_MS = 1500;  // min gap between any two enemy attacks (arcade fairness)
// The boss fight happens on the move: the car slows (but never stops) while
// the gunship lives. Reach the finish anyway and it escapes — no bonus.
export const BOSS_SPEED_SCALE = 0.65;

// Matchmaking
export const RECONNECTION_TIMEOUT_MS = 15_000;
export const COUNTDOWN_SECONDS = 3;

// Scoring
export const COMBO_WINDOW_MS = 3_000;
export const MAX_COMBO_MULTIPLIER = 8;
export const BEAT_RECOVERY_HEALTH = 15;

// Difficulty scaling per completed level
export const LEVEL_HEALTH_SCALE = 0.3;   // +30% enemy health per level
export const LEVEL_DAMAGE_SCALE = 0.2;   // +20% enemy damage per level

export interface DifficultyTuning {
  enemyHealthScale: number;
  enemyDamageScale: number;
  timeLimitScale: number;
}

/** Difficulty is independent from stage order; later stages get authored complexity, not hidden stat inflation. */
export const DIFFICULTY_TUNING: Record<DifficultyId, DifficultyTuning> = {
  [DifficultyId.Rookie]: { enemyHealthScale: 0.82, enemyDamageScale: 0.45, timeLimitScale: 1.12 },
  [DifficultyId.Agent]: { enemyHealthScale: 1, enemyDamageScale: 0.65, timeLimitScale: 1 },
  [DifficultyId.Veteran]: { enemyHealthScale: 1.25, enemyDamageScale: 1, timeLimitScale: 0.92 },
};
